/* eslint-disable @typescript-eslint/member-ordering */

import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AppConfigModel, AppThemeName } from '@libs/cms/models';
import { AuthService } from '@libs/shared/auth';
import { CmsLanguageService } from '@libs/shared/common';
import { CustomerModel } from '@libs/account/models';
import { combineLatest, defer, map, Observable, shareReplay, switchMap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CmsService } from '../cms';

const CONFIG_CACHE_TTL_MS: number = 15 * 60 * 1000; // 15 minutes, set 0 to disable cache

interface ConfigCacheEntry {
  readonly config$: Observable<AppConfigModel>;
  readonly createdAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  private readonly authService: AuthService = inject(AuthService);
  private readonly cmsService: CmsService = inject(CmsService);
  private readonly cmsLanguageService: CmsLanguageService = inject(CmsLanguageService);

  /** Config storage is an application configuration local cache.
   * Usage: `this.configStorage[language][customerId] = configCacheEntry;`
   * */
  private readonly configStorage: Record<string, Record<string, ConfigCacheEntry>> = {};

  private readonly language$: Observable<string | undefined> = toObservable(this.cmsLanguageService.language);

  private readonly customer$: Observable<CustomerModel | undefined> = toObservable(this.authService.isUpdating).pipe(
    filter(isUpdating => !isUpdating),
    switchMap(() => this.authService.getCurrentCustomer()),
  );

  private readonly config$: Observable<AppConfigModel> = defer(() => {
    return combineLatest([this.language$, this.customer$]).pipe(
      switchMap(([lang, customer]) => {
        return this.loadConfig(lang ?? '', String(customer?.id));
      }),
    );
  }).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly userTheme$: Observable<AppThemeName> = this.config$.pipe(
    map((config: AppConfigModel): AppThemeName => config.theme.name),
  );

  private loadConfig(
    language: string,
    customerId: string,
  ): Observable<AppConfigModel> {
    const now: number = Date.now();
    const cachedConfig: ConfigCacheEntry | undefined = this.configStorage[language]?.[customerId];

    if (cachedConfig && now - cachedConfig.createdAt < CONFIG_CACHE_TTL_MS) {
      return cachedConfig.config$;
    }

    const config$: Observable<AppConfigModel> = defer(() => {
      return this.cmsService.getAppConfig();
    }).pipe(
      // Cancel the previous config request when the language changes.
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.configStorage[language] = {
      ...this.configStorage[language],
      [customerId]: {
        config$,
        createdAt: now,
      },
    };

    return config$;
  }

  public getTheme(): Observable<AppThemeName> {
    return this.userTheme$;
  }

  public getConfig(): Observable<AppConfigModel> {
    return this.config$;
  }
}
